import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { ExternalLink } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { formatCost, getProviderBadgeClasses, resolveModality } from "../lib/utils";
import { ProviderLogo } from './ui/ProviderLogo';

export const TraceTable = ({ traces = [], onTraceClick }) => {
  const navigate = useNavigate();

  const getStatusBadge = (status) => {
    const statusLower = String(status).toLowerCase();
    if (statusLower === 'success' || status === '200') {
      return <span className="inline-flex px-1.5 py-px rounded text-[10px] font-mono font-medium bg-green-500/15 text-green-400">ok</span>;
    }
    if (statusLower === 'error' || status === '500') {
      return <span className="inline-flex px-1.5 py-px rounded text-[10px] font-mono font-medium bg-red-500/15 text-red-400">err</span>;
    }
    return <span className="inline-flex px-1.5 py-px rounded text-[10px] font-mono font-medium bg-zinc-500/15 text-zinc-400">{status}</span>;
  };

  if (traces.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <p className="text-sm text-zinc-500 font-mono">No traces found</p>
        <p className="text-xs text-zinc-600 mt-1">
          Traces appear here when your SDK starts sending data
        </p>
      </div>
    );
  }

  return (
    <div className="rounded border border-white/[0.06] overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="border-white/[0.06] hover:bg-transparent">
            <TableHead className="text-[10px] text-zinc-500 font-mono uppercase tracking-wider py-2 px-3">Time</TableHead>
            <TableHead className="text-[10px] text-zinc-500 font-mono uppercase tracking-wider py-2 px-3">Trace</TableHead>
            <TableHead className="text-[10px] text-zinc-500 font-mono uppercase tracking-wider py-2 px-3">Provider</TableHead>
            <TableHead className="text-[10px] text-zinc-500 font-mono uppercase tracking-wider py-2 px-3">Goal</TableHead>
            <TableHead className="text-[10px] text-zinc-500 font-mono uppercase tracking-wider py-2 px-3">Model</TableHead>
            <TableHead className="text-[10px] text-zinc-500 font-mono uppercase tracking-wider py-2 px-3">Duration</TableHead>
            <TableHead className="text-[10px] text-zinc-500 font-mono uppercase tracking-wider py-2 px-3">Cost</TableHead>
            <TableHead className="text-[10px] text-zinc-500 font-mono uppercase tracking-wider py-2 px-3">Spans</TableHead>
            <TableHead className="text-[10px] text-zinc-500 font-mono uppercase tracking-wider py-2 px-3">Status</TableHead>
            <TableHead className="text-[10px] text-zinc-500 font-mono uppercase tracking-wider py-2 px-3"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {traces.map((trace) => {
            const modelId = trace.model_id || trace.endpoint || "";
            const { provider } = getProviderBadgeClasses(modelId);
            return (
              <TableRow
                key={trace.trace_id}
                className="border-white/[0.04] hover:bg-white/[0.02]"
              >
                <TableCell className="font-mono text-[11px] text-zinc-500 py-2 px-3">
                  {trace.ts_start
                    ? formatDistanceToNow(new Date(trace.ts_start), { addSuffix: true })
                    : 'N/A'}
                </TableCell>
                <TableCell className="font-mono text-[11px] text-zinc-600 py-2 px-3">
                  {trace.trace_id.substring(0, 8)}
                </TableCell>
                <TableCell className="py-2 px-3">
                  <span className="flex items-center gap-1.5">
                    <ProviderLogo provider={modelId} size={14} />
                    <span className="text-[11px] text-zinc-400 font-mono">{provider !== 'unknown' ? provider : ''}</span>
                  </span>
                </TableCell>
                <TableCell className="py-2 px-3">
                  {trace.goal_id ? (
                    <span className="font-mono text-[11px] px-1.5 py-0.5 rounded bg-zinc-800/60 border border-zinc-700/50 text-zinc-300">
                      {trace.goal_id}
                    </span>
                  ) : (
                    <span className="text-zinc-700">—</span>
                  )}
                </TableCell>
                <TableCell className="font-mono text-[11px] text-zinc-400 py-2 px-3">{trace.endpoint}</TableCell>
                <TableCell className="font-mono text-[11px] text-zinc-500 py-2 px-3">{trace.total_duration_ms}ms</TableCell>
                <TableCell className="font-mono text-[11px] text-zinc-400 py-2 px-3">{formatCost(trace.total_cost)}</TableCell>
                <TableCell className="font-mono text-[11px] text-zinc-600 py-2 px-3">{trace.span_count || 1}</TableCell>
                <TableCell className="py-2 px-3">{getStatusBadge(trace.status)}</TableCell>
                <TableCell className="py-2 px-3">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/traces/${trace.trace_id}`);
                    }}
                    className="text-zinc-500 hover:text-white transition-colors"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                  </button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};
